import React, { useState, useContext, useEffect } from 'react';
import { Button, ListGroup, Badge, Modal, Card, Row, Col, Spinner } from 'react-bootstrap';
import { NotificationManager } from 'react-notifications';
import { FiArrowLeft, FiUsers, FiClock, FiCalendar, FiUserPlus, FiMapPin, FiX, FiChevronRight } from 'react-icons/fi';
import { FaBus } from 'react-icons/fa';
import moment from 'moment';
import { useNavigate, useParams } from 'react-router-dom';

import { AppContext } from '../AppContext';
import { tre } from '../trebus';
import ZList from './../components/ZList';
import TravellerList from './../components/TravellerList';

export default function Trip() {
    const { id } = useParams();
    const context = useContext(AppContext);
    const [trip, setTrip] = useState({ route: { name: '' } });
    const [showSelectUser, setShowSelectUser] = useState(false);
    const [travellers, setTravellers] = useState([]);
    const [idStart, setIdStart] = useState();
    const [stops, setStops] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    const loadTripData = () => {
        if (id) {
            setIsLoading(true);
            let loadedTrip = false;
            let loadedTravellers = false;

            const checkLoaded = () => {
                if (loadedTrip && loadedTravellers) {
                    setIsLoading(false);
                }
            };

            tre.api('GET', `/trips/${id}`, {
                context: context,
                success: (r) => {
                    setTrip(r);
                    loadedTrip = true;
                    checkLoaded();
                },
                error: () => {
                    loadedTrip = true;
                    checkLoaded();
                }
            });

            tre.api('GET', `/trips/${id}/travellers`, {
                context: context,
                success: (r) => {
                    setTravellers(r);
                    const stopMap = {};
                    r.forEach(traveller => {
                        const stop = traveller.start;
                        if (stop) {
                            stopMap[stop.id] = stopMap[stop.id] || { ...stop, numTravellers: 0 };
                            stopMap[stop.id].numTravellers++;
                        }
                    });
                    setStops(stopMap);
                    loadedTravellers = true;
                    checkLoaded();
                },
                error: () => {
                    loadedTravellers = true;
                    checkLoaded();
                }
            });
        }
    };

    useEffect(() => {
        loadTripData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const addTraveller = ({ user, tripReturn }) => {
        tre.api('POST', `/trips/${trip.id}/users/${user.id}`, {
            context: context,
            params: { idTripReturn: tripReturn ? tripReturn.id : null },
            success: () => {
                NotificationManager.success(`Añadido el usuario ${user.name} al viaje`);
                loadTripData();
            },
        });
    };

    const handleConfirm = (travellerId, newConfirmed) => {
        setTravellers(prev => prev.map(t =>
            t.id === travellerId ? { ...t, confirmed: newConfirmed } : t
        ));
    };

    const totalTravellers = Object.values(stops).reduce((sum, stop) => sum + stop.numTravellers, 0);

    // Loading state
    if (isLoading) {
        return (
            <div className="container py-4" style={{ maxWidth: '900px' }}>
                <div className="text-center py-5">
                    <Spinner animation="border" style={{ color: '#1a73e8' }} />
                    <p className="mt-3 text-muted">Cargando viaje...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4" style={{ maxWidth: '900px' }}>
            {/* Back Button */}
            <Button
                variant="link"
                onClick={() => idStart ? setIdStart(null) : navigate('/journey')}
                className="mb-3 p-0 d-flex align-items-center"
                style={{ color: '#5f6368', textDecoration: 'none', fontWeight: 500 }}
            >
                <FiArrowLeft className="me-2" size={18} />
                {idStart ? 'Volver al listado de paradas' : 'Volver a viajes'}
            </Button>

            {/* Trip Header Card */}
            <Card className="border-0 mb-4" style={{ borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                <Card.Header
                    className="py-3 px-4"
                    style={{
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #e9ecef',
                        borderRadius: '12px 12px 0 0'
                    }}
                >
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                        <div className="d-flex align-items-center">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                style={{
                                    width: '48px',
                                    height: '48px',
                                    backgroundColor: trip.route.direction === 'GO' ? '#e8f0fe' : '#e6f4ea'
                                }}
                            >
                                <FaBus
                                    size={22}
                                    style={{ color: trip.route.direction === 'GO' ? '#1a73e8' : '#34a853' }}
                                />
                            </div>
                            <div>
                                <h5 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>
                                    {trip.route.name}
                                </h5>
                                <small style={{ color: '#5f6368' }}>
                                    {trip.route.direction === 'GO' ? 'Viaje de ida' : 'Viaje de vuelta'}
                                </small>
                            </div>
                        </div>
                        <Button
                            onClick={() => setShowSelectUser(true)}
                            style={{
                                backgroundColor: '#1a73e8',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '10px 20px',
                                fontWeight: 500
                            }}
                            className="d-flex align-items-center"
                        >
                            <FiUserPlus className="me-2" size={18} />
                            Añadir pasajero
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body className="p-4">
                    <Row className="g-4">
                        <Col xs={12} sm={4}>
                            <div className="d-flex align-items-center">
                                <div
                                    className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                    style={{ width: '40px', height: '40px', backgroundColor: '#e8f0fe' }}
                                >
                                    <FiCalendar style={{ color: '#1a73e8' }} size={20} />
                                </div>
                                <div>
                                    <small style={{ color: '#5f6368' }} className="d-block">Fecha</small>
                                    <strong style={{ color: '#202124' }}>{moment(trip.date).format('DD MMM YYYY')}</strong>
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} sm={4}>
                            <div className="d-flex align-items-center">
                                <div
                                    className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                    style={{ width: '40px', height: '40px', backgroundColor: '#e6f4ea' }}
                                >
                                    <FiClock style={{ color: '#34a853' }} size={20} />
                                </div>
                                <div>
                                    <small style={{ color: '#5f6368' }} className="d-block">Hora de salida</small>
                                    <strong style={{ color: '#202124' }}>{moment(trip.route.departure, 'HH:mm:ss').format('H:mm')}</strong>
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} sm={4}>
                            <div className="d-flex align-items-center">
                                <div
                                    className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                    style={{ width: '40px', height: '40px', backgroundColor: '#fef7e0' }}
                                >
                                    <FiUsers style={{ color: '#f9ab00' }} size={20} />
                                </div>
                                <div>
                                    <small style={{ color: '#5f6368' }} className="d-block">Pasajeros</small>
                                    <strong style={{ color: '#202124' }}>{totalTravellers} personas</strong>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {!idStart ? (
                /* Stops List */
                <Card className="border-0" style={{ borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                    <Card.Header
                        className="py-3 px-4"
                        style={{
                            backgroundColor: '#f8f9fa',
                            borderBottom: '1px solid #e9ecef',
                            borderRadius: '12px 12px 0 0'
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                style={{ width: '36px', height: '36px', backgroundColor: '#e6f4ea' }}
                            >
                                <FiMapPin style={{ color: '#34a853' }} size={18} />
                            </div>
                            <h6 className="mb-0" style={{ color: '#202124', fontWeight: 500 }}>
                                Paradas y Pasajeros
                            </h6>
                        </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                        {Object.keys(stops).length > 0 ? (
                            <ListGroup variant="flush">
                                {Object.keys(stops).map((stopId, index) => (
                                    <ListGroup.Item
                                        key={stopId}
                                        action
                                        onClick={() => setIdStart(stopId)}
                                        className="py-3 px-4"
                                        style={{
                                            borderLeft: '3px solid transparent',
                                            transition: 'all 0.2s ease'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.borderLeftColor = '#34a853'}
                                        onMouseLeave={(e) => e.currentTarget.style.borderLeftColor = 'transparent'}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                                    style={{
                                                        width: '36px',
                                                        height: '36px',
                                                        backgroundColor: '#e6f4ea',
                                                        color: '#34a853',
                                                        fontWeight: 600,
                                                        fontSize: '14px'
                                                    }}
                                                >
                                                    {index + 1}
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 500, color: '#202124' }}>
                                                        {stops[stopId].name}
                                                    </div>
                                                    <small style={{ color: '#5f6368' }}>
                                                        Pulsa para ver pasajeros
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center">
                                                <span
                                                    className="d-flex align-items-center"
                                                    style={{
                                                        backgroundColor: '#1a73e8',
                                                        color: 'white',
                                                        padding: '8px 14px',
                                                        borderRadius: '20px',
                                                        fontSize: '0.85rem',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    <FiUsers className="me-1" />
                                                    {stops[stopId].numTravellers}
                                                </span>
                                                <FiChevronRight className="ms-2" style={{ color: '#5f6368' }} />
                                            </div>
                                        </div>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        ) : (
                            <div className="text-center py-5">
                                <div
                                    className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                    style={{ width: '80px', height: '80px', backgroundColor: '#f8f9fa' }}
                                >
                                    <FiUsers size={36} style={{ color: '#dadce0' }} />
                                </div>
                                <p className="text-muted mb-4">
                                    No hay pasajeros para recoger en ninguna de las paradas.
                                </p>
                                <Button
                                    onClick={() => setShowSelectUser(true)}
                                    style={{
                                        backgroundColor: '#1a73e8',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '12px 24px',
                                        fontWeight: 500
                                    }}
                                >
                                    <FiUserPlus className="me-2" />
                                    Añadir pasajero
                                </Button>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            ) : (
                /* Traveller List for selected stop */
                <TravellerList
                    travList={travellers}
                    onChange={loadTripData}
                    onConfirm={handleConfirm}
                    idStart={idStart}
                    stopName={stops[idStart]?.name}
                />
            )}

            <SelectUserWithComeBack
                show={showSelectUser}
                date={trip.date}
                toggle={() => setShowSelectUser(!showSelectUser)}
                onSelect={addTraveller}
            />
        </div>
    );
}

/**
 * Modal to select user and return trip
 */
function SelectUserWithComeBack({ show, date, toggle, onSelect }) {
    const context = useContext(AppContext);
    const [users, setUsers] = useState({});
    const [selectedUser, setSelectedUser] = useState();
    const [tripsReturn, setTripsReturn] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (show) setSelectedUser(null);
    }, [show]);

    useEffect(() => {
        if (show && date) {
            setIsLoading(true);
            tre.api('GET', '/trips/journeys', {
                context: context,
                params: { date: date },
                success: (r) => {
                    setTripsReturn(r);
                    setIsLoading(false);
                },
                error: () => setIsLoading(false)
            });
        }
    }, [show, date, context]);

    const loadUsers = (p = { page: 1, search: '' }) => {
        tre.api('GET', `/companies/${context.idCompany}/users`, {
            context: context,
            params: { page: p.page, rows: 8, name: p.search },
            success: (r) => setUsers(r),
        });
    };

    const setTripReturn = (trip) => {
        onSelect({ user: selectedUser, tripReturn: trip });
        setSelectedUser(null);
    };

    return (
        <Modal size="lg" show={show} onHide={toggle} centered>
            <Modal.Header
                closeButton
                style={{
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #e9ecef'
                }}
            >
                <Modal.Title className="d-flex align-items-center" style={{ color: '#202124', fontWeight: 500 }}>
                    <div
                        className="d-flex align-items-center justify-content-center rounded-circle me-3"
                        style={{ width: '40px', height: '40px', backgroundColor: '#e8f0fe', color: '#1a73e8' }}
                    >
                        {!selectedUser ? <FiUserPlus size={18} /> : <FiClock size={18} />}
                    </div>
                    {!selectedUser ? 'Añadir Pasajero' : 'Seleccionar Viaje de Vuelta'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
                {!selectedUser ? (
                    <ZList
                        search
                        rows={users.data}
                        page={users.current_page}
                        pages={users.last_page}
                        count={users.total}
                        onFetch={loadUsers}
                        onSelect={setSelectedUser}
                        showHeaders={true}
                        cols={[
                            {
                                field: 'full_name',
                                caption: 'Usuario',
                                align: 'L',
                                md: 6,
                                value: r => (
                                    <div className="d-flex align-items-center">
                                        <div
                                            className="d-flex align-items-center justify-content-center rounded-circle me-2"
                                            style={{
                                                width: '32px',
                                                height: '32px',
                                                backgroundColor: '#e8f0fe',
                                                color: '#1a73e8',
                                                fontSize: '13px',
                                                fontWeight: 600
                                            }}
                                        >
                                            {(r.name || '?').charAt(0).toUpperCase()}
                                        </div>
                                        <span style={{ fontWeight: 500, color: '#202124' }}>
                                            {r.surname ? `${r.surname}, ${r.name}` : r.name}
                                        </span>
                                    </div>
                                )
                            },
                            {
                                field: 'go',
                                caption: 'Ida',
                                md: 3,
                                value: r => r.go ? (
                                    <span
                                        style={{
                                            backgroundColor: '#e8f0fe',
                                            color: '#1a73e8',
                                            padding: '4px 10px',
                                            borderRadius: '12px',
                                            fontSize: '13px',
                                            fontWeight: 500
                                        }}
                                    >
                                        {r.go.name}
                                    </span>
                                ) : <span className="text-muted">-</span>
                            },
                            {
                                field: 'come',
                                caption: 'Vuelta',
                                md: 3,
                                value: r => r.come ? (
                                    <span
                                        style={{
                                            backgroundColor: '#e6f4ea',
                                            color: '#34a853',
                                            padding: '4px 10px',
                                            borderRadius: '12px',
                                            fontSize: '13px',
                                            fontWeight: 500
                                        }}
                                    >
                                        {r.come.name}
                                    </span>
                                ) : <span className="text-muted">-</span>
                            },
                        ]}
                    />
                ) : (
                    <>
                        <div
                            className="d-flex align-items-center p-3 mb-4"
                            style={{
                                backgroundColor: '#e6f4ea',
                                borderRadius: '12px',
                                border: '1px solid #34a853'
                            }}
                        >
                            <div
                                className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                style={{ width: '36px', height: '36px', backgroundColor: '#34a853' }}
                            >
                                <FiUserPlus style={{ color: 'white' }} size={18} />
                            </div>
                            <span style={{ color: '#202124' }}>
                                Has seleccionado a <strong>{selectedUser.full_name}</strong> para la ida
                            </span>
                        </div>

                        {isLoading ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" style={{ color: '#1a73e8' }} />
                                <p className="mt-3 text-muted">Cargando viajes de vuelta...</p>
                            </div>
                        ) : (
                            <ListGroup style={{ borderRadius: '12px', overflow: 'hidden' }}>
                                {tripsReturn
                                    .filter(trip => trip.route.direction === 'COME')
                                    .map(trip => (
                                        <ListGroup.Item
                                            key={trip.id}
                                            action
                                            onClick={() => setTripReturn(trip)}
                                            className="py-3 px-4"
                                            style={{
                                                borderLeft: '3px solid transparent',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.borderLeftColor = '#34a853'}
                                            onMouseLeave={(e) => e.currentTarget.style.borderLeftColor = 'transparent'}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <div className="d-flex align-items-center">
                                                    <div
                                                        className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                                        style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            backgroundColor: '#e6f4ea',
                                                            color: '#34a853'
                                                        }}
                                                    >
                                                        <FaBus size={16} />
                                                    </div>
                                                    <span style={{ fontWeight: 500, color: '#202124' }}>{trip.route.name}</span>
                                                </div>
                                                <Badge
                                                    style={{
                                                        backgroundColor: '#e6f4ea',
                                                        color: '#34a853',
                                                        padding: '6px 12px',
                                                        borderRadius: '8px',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {moment(trip.route.departure, 'HH:mm').format('HH:mm')}
                                                </Badge>
                                            </div>
                                        </ListGroup.Item>
                                    ))}
                                <ListGroup.Item
                                    action
                                    onClick={() => setTripReturn(null)}
                                    className="py-3 px-4"
                                    style={{ backgroundColor: '#f8f9fa' }}
                                >
                                    <div className="d-flex align-items-center" style={{ color: '#5f6368' }}>
                                        <FiClock className="me-2" />
                                        No se sabe cuándo vuelve
                                    </div>
                                </ListGroup.Item>
                            </ListGroup>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer style={{ backgroundColor: '#f8f9fa', borderTop: '1px solid #e9ecef' }}>
                <Button
                    variant="light"
                    onClick={toggle}
                    style={{ borderRadius: '8px', fontWeight: 500 }}
                >
                    <FiX className="me-2" />
                    Cancelar
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
